/* ============================================================
   cx-quiz.js — quiz state machine, navigation, Q8 timer, cart
   No shared selectors with bundlebox JS. cx-quiz scope only.
   ============================================================ */
(function () {
  "use strict";

  const SCREENS = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9"];
  const LOADING_DELAY_MS = 3400;

  /* Tag config — qty stays 1, only the tier (PRIMARY/RECOMMENDED/OPTIONAL) flexes by answers. */
  const PRODUCT_TAGS = {
    sleep:      { defaultQty: 1 },
    essentials: { defaultQty: 1 },
    energy:     { defaultQty: 1 },
    beauty:     { defaultQty: 1 }
  };

  /* Cadence label, always paired with the dynamic tier prefix */
  const CADENCE = {
    sleep:      "10PM EVERY NIGHT",
    essentials: "9AM DAILY",
    energy:     "2PM DAILY",
    beauty:     "9AM DAILY"
  };

  /* Recommendation rules (deterministic):
       - Sleep      = always PRIMARY · 10PM EVERY NIGHT, qty 1
       - Essentials = always RECOMMENDED · 9AM DAILY, qty 1
       - Energy     = default RECOMMENDED · 2PM DAILY, qty 1
                      ESCALATION: Q2=yes-quite-often AND Q4=yes-too-many
                        → PRIMARY · 7AM and 2PM DAILY, qty 2
       - Beauty     = RECOMMENDED · 9AM DAILY if Q5=want-better, else OPTIONAL · 9AM DAILY, qty 1
                      (always rendered LAST in the tile list)
  */
  function computeRecommendation(answers) {
    const energyEscalated = answers.q2 === "yes-quite-often" && answers.q4 === "yes-too-many";
    const beautyWanted    = answers.q5 === "want-better";
    return {
      tiers: {
        sleep:      "PRIMARY",
        essentials: "RECOMMENDED",
        energy:     energyEscalated ? "PRIMARY" : "RECOMMENDED",
        beauty:     beautyWanted ? "RECOMMENDED" : "OPTIONAL"
      },
      cadenceOverrides: energyEscalated ? { energy: "7AM and 2PM DAILY" } : {},
      qty: {
        sleep: 1,
        essentials: 1,
        energy: energyEscalated ? 2 : 1,
        beauty: beautyWanted ? 1 : 0
      },
      // Eligibility is ONLY set by the quiz answer match — never by stepper bumps.
      // Receipt also gates on energy qty >= 2 AND subscription, so single-product manual
      // qty changes can't unlock the bonus.
      cleanEnergyEligible: energyEscalated
    };
  }

  function applyRecommendation(root, rec) {
    Object.keys(rec.tiers).forEach(product => {
      const tagEl = root.querySelector('[data-cx-tag="' + product + '"]');
      const cadence = rec.cadenceOverrides[product] || CADENCE[product];
      if (tagEl) tagEl.textContent = rec.tiers[product] + " · " + cadence;
    });
    // When Energy is escalated to PRIMARY, render it above Essentials.
    if (rec.tiers.energy === "PRIMARY") {
      const energyTile = root.querySelector('[data-cx-tile][data-product="energy"]');
      const essTile    = root.querySelector('[data-cx-tile][data-product="essentials"]');
      if (energyTile && essTile && energyTile.parentNode === essTile.parentNode) {
        essTile.parentNode.insertBefore(energyTile, essTile);
      }
    }
  }

  // Swap Q2-Q6 lifestyle imagery based on Q1 gender answer.
  // Default markup uses women's photos; on "man" we swap to data-cx-media-m.
  // "prefer-not-to-say" keeps the default.
  function applyGenderImagery(root, q1Value) {
    const useMale = q1Value === "man";
    root.querySelectorAll("[data-cx-media-w][data-cx-media-m]").forEach(el => {
      const url = useMale ? el.getAttribute("data-cx-media-m") : el.getAttribute("data-cx-media-w");
      if (url) el.style.backgroundImage = "url('" + url + "')";
    });
  }

  // Force-fetch every Q2-Q6 lifestyle image (both genders) on init so they're
  // in the browser cache before the screen becomes visible. CSS background-image
  // alone defers the fetch until the element is rendered → visible mid-flow lag.
  function preloadLifestyleImagery(root) {
    const urls = new Set();
    root.querySelectorAll("[data-cx-media-w]").forEach(el => {
      const u = el.getAttribute("data-cx-media-w"); if (u) urls.add(u);
    });
    root.querySelectorAll("[data-cx-media-m]").forEach(el => {
      const u = el.getAttribute("data-cx-media-m"); if (u) urls.add(u);
    });
    urls.forEach(url => { const img = new Image(); img.src = url; });
  }

  /* Cart payload pattern */
  const DISCOUNT_CODES = {
    sub_save:        "NUSUB25",
    bundle_2:        "NUBUNDLE2",
    bundle_3:        "NUBUNDLE3",
    welcome:         "GET15",
    wellness_full:   "NUWELL23",   // cx-quiz only: 4 unique products → $23 off (must register in Shopify Admin)
    wellness_three:  "NUWELL15",   // 3 unique products → $15 off (original bundlebox code)
    clean_energy:    "NUCLEAN15"   // energy escalation gated bonus → $15 off (must register in Shopify Admin)
  };

  /* Shopify variant + selling-plan map. Can be overridden by window.CX_QUIZ_CONFIG
     (set by Liquid template at runtime). Falls back to these defaults for local preview. */
  const PRODUCT_CONFIG_DEFAULT = {
    shop_domain:        "nustrips.myshopify.com",
    monthly_selling_plan: "4617470167",
    variants: {
      sleep:      "44052420755671",
      essentials: "46996555563223",
      energy:     "48418858827991",
      beauty:     "44044917375191"
    }
  };

  function init(root) {
    if (!root) return;
    const stage   = root.querySelector("[data-cx-stage]");
    const screens = Array.from(root.querySelectorAll("[data-cx-screen]"));
    const state   = { idx: 0, answers: {}, qty: {}, stepEnteredAt: Date.now(), cleanEnergyEligible: false, subscribe: true };

    Object.keys(PRODUCT_TAGS).forEach(k => state.qty[k] = PRODUCT_TAGS[k].defaultQty);

    // Kick off lifestyle-image warmup immediately so Q2-Q6 backgrounds are
    // already in cache by the time each screen activates.
    preloadLifestyleImagery(root);

    // Tracker helper (no-op if cxTrack not loaded)
    const track = (name, payload) => {
      try { if (window.cxTrack) window.cxTrack(name, payload); } catch (e) {}
    };

    // Fire cx_quiz_started on init (Q1 is first active screen)
    track("cx_quiz_started", {});
    track("cx_quiz_step_viewed", { step: "q1" });

    // Page-unload abandonment detection
    window.addEventListener("beforeunload", function () {
      const reached = window.cxQuizResultsViewed && window.cxQuizResultsViewed();
      if (!reached) {
        track("cx_quiz_abandoned", {
          last_step: SCREENS[state.idx],
          time_in_session_ms: Date.now() - (state.stepEnteredAt || Date.now())
        });
      }
    });

    function go(targetId) {
      const nextIdx = SCREENS.indexOf(targetId);
      if (nextIdx === -1) return;
      const next = screens[nextIdx];
      if (!next) return;
      const cur = screens[state.idx];
      if (next === cur) return;

      // Clean slate: remove is-active + is-leaving from all screens, then activate target
      screens.forEach(s => {
        if (s === next) return;
        s.classList.remove("is-active");
        s.classList.remove("is-leaving");
      });
      next.classList.remove("is-leaving");
      next.classList.add("is-active");

      const prevIdx = state.idx;
      state.idx = nextIdx;
      updateProgress();

      // Tracker — step_viewed every advance
      track("cx_quiz_step_viewed", { step: targetId });
      state.stepEnteredAt = Date.now();

      if (targetId === "q7") {
        // Q6 just answered → lock the final cart shape so Q7's "Unlock upto X% OFF"
        // copy reflects the SAME number Q9 will show (post-escalation, post-cleanEnergy).
        const rec = computeRecommendation(state.answers);
        state.cleanEnergyEligible = !!rec.cleanEnergyEligible;
        Object.keys(rec.qty).forEach(p => { state.qty[p] = rec.qty[p]; });
        lockApprovedPct();
      }

      if (targetId === "q8") {
        track("cx_quiz_loading_viewed", {});
        setTimeout(() => go("q9"), LOADING_DELAY_MS);
      }

      if (targetId === "q9") {
        const rec = computeRecommendation(state.answers);
        // Lock eligibility ONCE from the answers — never re-evaluated after stepper changes.
        state.cleanEnergyEligible = !!rec.cleanEnergyEligible;
        // Apply qty overrides (e.g. energy escalation → qty 2) to state + UI
        Object.keys(rec.qty).forEach(p => {
          state.qty[p] = rec.qty[p];
          const qtyEl = root.querySelector('[data-cx-qty="' + p + '"]');
          if (qtyEl) qtyEl.textContent = rec.qty[p];
        });
        applyRecommendation(root, rec);
        updateReceipt();
        lockApprovedPct();  // recompute % off vs the recommended cart (post-escalation)
        // Warm Shopify cart: 4 items + 3 codes attached, so any PDP click in
        // the slide cart already shows the full discounted state. Fire-and-forget.
        (async () => {
          try {
            const cfg = (window.CX_QUIZ_CONFIG && window.CX_QUIZ_CONFIG.variants) ? window.CX_QUIZ_CONFIG : PRODUCT_CONFIG_DEFAULT;
            const wItems = Object.keys(state.qty).filter(k => state.qty[k] > 0).map(p => ({ product: p, qty: state.qty[p] }));
            const wCodes = computeCartDiscountCodes();
            await warmCart(cfg, wItems, !!state.subscribe, wCodes);
          } catch (e) { console.warn("[cx-quiz] warmCart wrapper err:", e); }
        })();
        track("cx_quiz_results_viewed", {
          recommended_stack: Object.keys(state.qty).filter(k => state.qty[k] > 0).join("+"),
          tiers: rec.tiers,
          qty: rec.qty,
          clean_energy_eligible: state.cleanEnergyEligible
        });
      }

      if (stage && stage.scrollIntoView) {
        stage.scrollIntoView({ block: "start", behavior: "instant" });
      }
    }

    function updateProgress() {
      const totalSteps = 6;
      const labelMap = { q1: 1, q2: 2, q3: 3, q4: 4, q5: 5, q6: 6 };
      const cur = SCREENS[state.idx];
      const stepNum = labelMap[cur];

      screens.forEach(s => {
        const fillEl = s.querySelector("[data-cx-progress-fill]");
        if (fillEl) {
          let pct = 0;
          if (cur === "q7" || cur === "q8" || cur === "q9") pct = 100;
          else if (stepNum) pct = (stepNum / totalSteps) * 100;
          fillEl.style.width = pct + "%";
        }
      });
    }

    /* === Tap handlers === */
    root.addEventListener("click", function (e) {
      const tap = e.target.closest("[data-cx-next]");
      if (tap) {
        e.preventDefault();
        const next = tap.getAttribute("data-cx-next");
        const screen = tap.closest("[data-cx-screen]");
        const screenId = screen ? screen.getAttribute("data-cx-screen") : null;
        const value = tap.getAttribute("data-cx-value") || (tap.textContent || "").trim();
        if (screenId && value) state.answers[screenId] = value;
        // Q1 = gender selection → swap Q2-Q6 lifestyle imagery to match
        if (screenId === "q1") applyGenderImagery(root, value);
        // Tracker — step_answered
        if (screenId && value) {
          track("cx_quiz_step_answered", {
            step: screenId,
            answer: value,
            step_dwell_ms: Date.now() - (state.stepEnteredAt || Date.now())
          });
        }
        go(next);
        return;
      }

      const stepBtn = e.target.closest("[data-cx-step]");
      if (stepBtn) {
        e.preventDefault();
        const product = stepBtn.getAttribute("data-cx-product");
        const dir = stepBtn.getAttribute("data-cx-step");
        let qty = state.qty[product] || 0;
        if (dir === "+") qty = Math.min(qty + 1, 9);
        else qty = Math.max(qty - 1, 0);
        state.qty[product] = qty;
        const qtyEl = root.querySelector('[data-cx-qty="' + product + '"]');
        if (qtyEl) qtyEl.textContent = qty;
        updateReceipt();
        return;
      }

      const subBtn = e.target.closest("[data-cx-sub-toggle]");
      if (subBtn) {
        e.preventDefault();
        state.subscribe = !state.subscribe;
        subBtn.setAttribute("aria-pressed", state.subscribe ? "true" : "false");
        updateReceipt();
        return;
      }

      const addBtn = e.target.closest("[data-cx-add-to-cart]");
      if (addBtn) {
        e.preventDefault();
        addToCart();
        return;
      }
    });

    /* === Q7 email submit === */
    const emailForm = root.querySelector("[data-cx-email-form]");
    if (emailForm) {
      emailForm.addEventListener("submit", function (e) {
        e.preventDefault();
        const emailInput = emailForm.querySelector('input[type="email"]');
        const email = emailInput ? emailInput.value : "";
        state.answers.email = email;
        // Tracker — email_captured (also fires Klaviyo identify/track + subscribe via edge fn)
        track("cx_quiz_email_captured", {
          email: email,
          full_answers: state.answers,
          recommended_stack: Object.keys(state.qty).filter(k => state.qty[k] > 0).join("+")
        });
        go("q8");
      });
    }

    /* === Receipt math — direct port of bundle-box.liquid compute() ===
       Only difference: cx-quiz has 4 products (Sleep/Essentials/Energy/Beauty)
       so wellness fires at uniqueCount === 4 (vs 3 in bundlebox).
       Bundle tier max stays 20% (3+ qty) as in bundlebox.
    */
    // Per-variant OTP + sub prices (live from Shopify Admin GraphQL, 2026-05-12).
    // Sleep/Essentials/Energy 30-day = $39.99 OTP / $28.99 sub (27.5% off, plan 4617470167).
    // Beauty 30-day (variant 44044917375191, SKU BTY-30) = $36.50 OTP / $28.99 sub (20.6% off, same plan).
    const PRICE_TABLE = {
      sleep:      { otp: 39.99, sub: 28.99 },
      essentials: { otp: 39.99, sub: 28.99 },
      energy:     { otp: 39.99, sub: 28.99 },
      beauty:     { otp: 36.50, sub: 28.99 }
    };
    const SUB_PCT_LABEL   = 25;
    const BUNDLE_2_PCT    = 10;
    const BUNDLE_3_PCT    = 20;
    const WELLNESS_BONUS_FULL  = 23;   // 4 unique products → bigger bonus (cx-quiz)
    const WELLNESS_BONUS_THREE = 15;   // 3 unique products → original bundlebox bonus
    const CLEAN_ENERGY_BONUS   = 15;
    const TOTAL_PRODUCTS       = 4;    // sleep + essentials + energy + beauty
    const round = n => Math.round(n * 100) / 100;
    const fmt = n => "$" + n.toFixed(2);

    function updateReceipt() {
      const lineWrap = root.querySelector("[data-cx-receipt-lines]");
      if (!lineWrap) return;

      // === DIRECT PORT of bundle-box.liquid compute() ===
      const productKeys = ["sleep", "essentials", "energy", "beauty"];
      const productLabels = {
        sleep: "Sleep × 30ct",
        essentials: "Essentials × 30ct",
        energy: "Energy × 30ct",
        beauty: "Beauty × 30ct"
      };
      const items = productKeys.filter(k => state.qty[k] > 0);
      const uniqueCount = items.length;
      const totalQty = items.reduce((s, k) => s + state.qty[k], 0);

      // Clear existing product lines
      while (lineWrap.firstChild) lineWrap.removeChild(lineWrap.firstChild);

      if (totalQty === 0) {
        setText(root, "[data-cx-subtotal]", "$0.00");
        setText(root, "[data-cx-total]", "$0.00");
        return;
      }

      // === Math (per-variant prices + subscribe toggle + clean-energy bonus) ===
      const subscribe      = !!state.subscribe;
      const subtotalOtp    = round(items.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0));
      const afterSubAmount = round(items.reduce((s, k) => s + state.qty[k] * (subscribe ? PRICE_TABLE[k].sub : PRICE_TABLE[k].otp), 0));
      const subSavings     = subscribe ? round(subtotalOtp - afterSubAmount) : 0;
      const bundlePct      = totalQty >= 3 ? BUNDLE_3_PCT
                            : (totalQty === 2 ? BUNDLE_2_PCT : 0);
      // PER-LINE FLOOR to mirror Shopify's NUBUNDLE3/2 application exactly.
      // Shopify floors each line's discount: $28.99 × 20% = $5.798 → $5.79 (NOT $5.80).
      // Whole-subtotal round overshoots by ~$0.01-$0.04 vs real checkout.
      const bundleAmt      = round(items.reduce((s, k) => {
        const linePrice = subscribe ? PRICE_TABLE[k].sub : PRICE_TABLE[k].otp;
        const perLineDisc = Math.floor(linePrice * (bundlePct / 100) * 100) / 100;
        return s + perLineDisc * state.qty[k];
      }, 0));
      const afterBundle    = round(afterSubAmount - bundleAmt);
      // Wellness tiers: 4 unique = $23 (NUWELL23), 3 unique = $15 (NUWELL15), else 0.
      const wellnessAmt    = uniqueCount === TOTAL_PRODUCTS   ? WELLNESS_BONUS_FULL
                            : uniqueCount === TOTAL_PRODUCTS - 1 ? WELLNESS_BONUS_THREE
                            : 0;
      const wellnessLabel  = uniqueCount === TOTAL_PRODUCTS   ? "− 🎁 Wellness Bonus (all 4 added)"
                            : uniqueCount === TOTAL_PRODUCTS - 1 ? "− 🎁 Wellness Bonus (3 added)"
                            : "− 🎁 Wellness Bonus";
      // Clean-energy bonus: ONLY if quiz answers triggered eligibility AND energy qty≥2 AND subscribed
      // AND subtotal ≥ $150 (so customers can't strip other products and still keep the bonus).
      const cleanEnergyAmt = (subscribe
                              && state.cleanEnergyEligible
                              && (state.qty.energy || 0) >= 2
                              && subtotalOtp >= 150)
                              ? CLEAN_ENERGY_BONUS : 0;
      const finalTotal     = round(afterBundle - wellnessAmt - cleanEnergyAmt);
      const totalSavings   = round(subtotalOtp - finalTotal);

      // === Render product lines (each at OTP — bundle-box parity) ===
      items.forEach(k => {
        const qty = state.qty[k];
        const label = qty > 1 ? `${productLabels[k]} (×${qty})` : productLabels[k];
        const row = document.createElement("div");
        row.className = "cx-r__line";
        const labelSpan = document.createElement("span");
        labelSpan.textContent = label;
        const valueSpan = document.createElement("span");
        valueSpan.textContent = fmt(round(qty * PRICE_TABLE[k].otp));
        row.appendChild(labelSpan);
        row.appendChild(valueSpan);
        lineWrap.appendChild(row);
      });

      // === Update discount totals (display values; visibility handled by row toggles) ===
      setText(root, "[data-cx-subtotal]",          fmt(subtotalOtp));
      setText(root, "[data-cx-bundle-label]",      bundlePct > 0 ? `− Bundle savings (${bundlePct}% off)` : "− Bundle savings");
      setText(root, "[data-cx-bundle-discount]",   bundleAmt    > 0 ? "−" + fmt(bundleAmt)   : "$0.00");
      setText(root, "[data-cx-sub-discount]",      subSavings   > 0 ? "−" + fmt(subSavings)  : "$0.00");
      setText(root, "[data-cx-wellness-label]",    wellnessLabel);
      setText(root, "[data-cx-wellness-discount]", wellnessAmt  > 0 ? "−" + fmt(wellnessAmt) : "$0.00");
      setText(root, "[data-cx-clean-discount]",    cleanEnergyAmt > 0 ? "−" + fmt(cleanEnergyAmt) : "$0.00");
      setText(root, "[data-cx-total]",             fmt(finalTotal) + (subscribe ? "/mo" : ""));
      setText(root, "[data-cx-strike]",            totalSavings > 0 ? fmt(subtotalOtp) : "");
      setText(root, "[data-cx-save]",              totalSavings > 0 ? `🎉 You save ${fmt(totalSavings)} ${subscribe ? "vs. one-time" : "today"}` : "");
      setText(root, "[data-cx-cta-total]",         `Complete Checkout  →`);

      // Toggle dynamic discount row visibility (matches bundlebox: lines only shown when > 0)
      const bundleRow = root.querySelector("[data-cx-bundle-row]");
      if (bundleRow) bundleRow.style.display = bundleAmt > 0 ? "" : "none";
      const wellnessRow = root.querySelector("[data-cx-wellness-row]");
      if (wellnessRow) wellnessRow.style.display = wellnessAmt > 0 ? "" : "none";
      const cleanRow = root.querySelector("[data-cx-clean-row]");
      if (cleanRow) cleanRow.style.display = cleanEnergyAmt > 0 ? "" : "none";
      const subRow = root.querySelector("[data-cx-sub-row]");
      if (subRow) subRow.style.display = subSavings > 0 ? "" : "none";

      // Mark each tile's qty state for the deselected (qty=0) styling
      productKeys.forEach(p => {
        const tile = root.querySelector('[data-cx-tile][data-product="' + p + '"]');
        if (tile) tile.setAttribute("data-qty", String(state.qty[p] || 0));
      });
    }

    function setText(scope, sel, value) {
      const el = scope.querySelector(sel);
      if (el) el.textContent = value;
    }

    function capitalize(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

    // Build the list of Shopify discount codes that should apply at checkout.
    // Mirrors the receipt math gates exactly — never apply a code on the cart if
    // updateReceipt() didn't surface its $ line.
    function computeCartDiscountCodes() {
      const codes = [];
      const items = Object.keys(state.qty).filter(k => state.qty[k] > 0);
      const uniqueCount = items.length;
      const totalQty = items.reduce((s, k) => s + state.qty[k], 0);
      const subtotalOtp = items.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0);
      // Bundle (works on subs AND OTP; on subs configured to recur per Shopify Admin)
      if (totalQty >= 3) codes.push(DISCOUNT_CODES.bundle_3);
      else if (totalQty === 2) codes.push(DISCOUNT_CODES.bundle_2);
      // Wellness — gated on uniqueCount; one-time only
      if (uniqueCount === 4) codes.push(DISCOUNT_CODES.wellness_full);
      else if (uniqueCount === 3) codes.push(DISCOUNT_CODES.wellness_three);
      // Clean Energy — must replay receipt-math gates exactly
      if (state.subscribe
          && state.cleanEnergyEligible
          && (state.qty.energy || 0) >= 2
          && subtotalOtp >= 150) {
        codes.push(DISCOUNT_CODES.clean_energy);
      }
      return codes;
    }

    // Build Shopify cart permalink:
    //   https://{shop}/cart/{vid}:{q},{vid}:{q}[?selling_plan=PID][&discount=CODE1,CODE2]
    // Works cross-origin (we redirect via location.assign). Single global selling_plan
    // applies to every line — all 4 products are in plan group 1962770647 ("1 pack every month").
    function buildCartUrl(cfg, items, subscribe, codes) {
      const lines = items.map(it => {
        const vid = cfg.variants[it.product];
        return vid ? (vid + ":" + it.qty) : null;
      }).filter(Boolean);
      if (!lines.length) return null;
      const params = [];
      if (subscribe && cfg.monthly_selling_plan) {
        params.push("selling_plan=" + encodeURIComponent(cfg.monthly_selling_plan));
      }
      if (codes.length) {
        params.push("discount=" + encodeURIComponent(codes.join(",")));
      }
      const qs = params.length ? "?" + params.join("&") : "";
      return "https://" + cfg.shop_domain + "/cart/" + lines.join(",") + qs;
    }

    // True when running on the live Shopify storefront (same-origin → can POST to /cart/add
     // with items[i][selling_plan]). False on localhost preview → falls back to permalink
     // (validates everything except selling-plan attachment).
    function isOnShopify() {
      const h = location.hostname || "";
      return h.endsWith(".myshopify.com") || h === "nustrips.com" || h.endsWith(".nustrips.com");
    }

    // Warm cart on Q9 entry so the slide cart on any PDP click already shows
    // 4 items + 3 codes + $78 total. Sequential /discount/X requests accumulate
    // in cart.discount_codes (verified via cxq_warm_cart_simulate.js — all 5
    // assertions pass: cart total = Q9 total = Shopify checkout total = $78.00,
    // exactly. Fire-and-forget; doesn't block Q9 paint, ~2s total.
    // Suppress the Upcart slide-cart popup that auto-opens on /cart/add.js.
    // Upcart exposes window.upcartCloseCart() as its global close API; we also
    // strip the open classes as a belt-and-suspenders and run on a short
    // interval to catch any delayed re-opens during the 5 cart fetches.
    function suppressSlideCart() {
      const close = () => {
        try {
          if (typeof window.upcartCloseCart === "function") window.upcartCloseCart();
          document.body.classList.remove(
            "upcartPopupShow", "upcart-show", "cart-drawer-open",
            "drawer-open", "mini-cart-open", "js-drawer-open"
          );
        } catch (e) {}
      };
      close();
      const id = setInterval(close, 150);
      setTimeout(() => clearInterval(id), 5000);
    }

    // CSS pre-emption mask — hides Upcart's drawer entirely during warmCart()
    // so the user never sees the open/close animation. Removed ~1.5s after
    // the last cart op so the next real cart click works normally.
    function maskUpcart(on) {
      const ID = "cx-quiz-upcart-mask";
      let el = document.getElementById(ID);
      if (on && !el) {
        el = document.createElement("style");
        el.id = ID;
        el.textContent =
          '[id^="upcart-"],[class*="upcart-cart"],[class*="upcart-popup"],' +
          '[class*="upcart-drawer"],#upcart-app,.upcart-checkout' +
          '{display:none!important;opacity:0!important;' +
          'transition:none!important;pointer-events:none!important;}';
        document.head.appendChild(el);
      } else if (!on && el) {
        el.remove();
      }
    }

    async function warmCart(cfg, items, subscribe, codes) {
      if (!isOnShopify()) return; // cross-origin preview can't touch the cart
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;
      maskUpcart(true);
      try {
        try {
          await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } });
        } catch (e) { console.warn("[cx-quiz] warmCart clear failed:", e); return; }
        suppressSlideCart();
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ items: adds })
          });
        } catch (e) { console.warn("[cx-quiz] warmCart add failed:", e); return; }
        suppressSlideCart();
        for (let i = 0; i < codes.length; i++) {
          if (i > 0) await new Promise(r => setTimeout(r, 800));
          try {
            await fetch("/discount/" + encodeURIComponent(codes[i]) + "?redirect=/cart.js", { redirect: "follow" });
          } catch (e) {
            console.warn("[cx-quiz] warmCart code " + codes[i] + " failed:", e);
          }
          suppressSlideCart();
        }
      } finally {
        setTimeout(() => maskUpcart(false), 1500);
      }
    }

    // Same-origin cart submit — mirrors bundle-box.liquid:449-510 pattern.
    // Required for selling_plan to attach per-line; cart permalink ?selling_plan=
    // is silently dropped by Shopify.
    async function submitCartFormPost(cfg, items, subscribe, codes) {
      const planId = cfg.monthly_selling_plan ? parseInt(cfg.monthly_selling_plan, 10) : null;
      const adds = items.map(it => {
        const vid = parseInt(cfg.variants[it.product], 10);
        if (!vid) return null;
        const line = { id: vid, quantity: it.qty };
        if (subscribe && planId) line.selling_plan = planId;
        return line;
      }).filter(Boolean);
      if (!adds.length) return;

      try {
        await fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } });
      } catch (e) {}

      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.style.display = "none";

      adds.forEach((item, i) => {
        Object.entries(item).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          const inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = "items[" + i + "][" + k + "]";
          inp.value = String(v);
          form.appendChild(inp);
        });
      });

      // Pass UTM/click params through to checkout (bundle-box parity)
      const trackingParams = new URLSearchParams();
      const passthrough = /^(utm_|gclid$|fbclid$|ttclid$|msclkid$|li_fat_id$|irclickid$|ref$)/i;
      new URLSearchParams(window.location.search).forEach((v, k) => {
        if (passthrough.test(k)) trackingParams.set(k, v);
      });
      // Stack multiple discount codes via comma-separated ?discount=A,B,C
      // (bundle-box pattern, verified to apply all codes whose combinesWith allows).
      if (codes.length) trackingParams.set("discount", codes.join(","));
      const qs = trackingParams.toString();

      const returnTo = document.createElement("input");
      returnTo.type = "hidden";
      returnTo.name = "return_to";
      returnTo.value = "/checkout" + (qs ? ("?" + qs) : "");
      form.appendChild(returnTo);

      document.body.appendChild(form);
      form.submit();
    }

    function addToCart() {
      const items = [];
      Object.keys(state.qty).forEach(p => {
        if (state.qty[p] > 0) items.push({ product: p, qty: state.qty[p] });
      });
      const totalEl = root.querySelector("[data-cx-total]");
      const savedEl = root.querySelector("[data-cx-save]");
      const parseDollar = el => el ? parseFloat((el.textContent || "0").replace(/[^0-9.]/g, "")) : 0;
      const cfg = (window.CX_QUIZ_CONFIG && window.CX_QUIZ_CONFIG.variants) ? window.CX_QUIZ_CONFIG : PRODUCT_CONFIG_DEFAULT;
      const codes = computeCartDiscountCodes();
      const onShopify = isOnShopify();
      // Permalink kept for cross-origin preview + tracker payload audit trail
      const cartUrl = buildCartUrl(cfg, items, !!state.subscribe, codes);

      track("cx_quiz_cart_added", {
        cart_payload: items,
        total: parseDollar(totalEl),
        savings: parseDollar(savedEl),
        codes: codes,
        subscribe: !!state.subscribe,
        cart_url: cartUrl,
        submit_mode: onShopify ? "form_post" : "permalink"
      });

      window.dispatchEvent(new CustomEvent("cx-quiz:add-to-cart", {
        detail: { items, answers: state.answers, codes: codes, subscribe: !!state.subscribe, cart_url: cartUrl }
      }));

      // Tiny delay so beacon/keepalive fetch to Supabase has a chance to send
      if (onShopify) {
        setTimeout(() => { submitCartFormPost(cfg, items, !!state.subscribe, codes); }, 50);
      } else if (cartUrl) {
        setTimeout(() => { window.location.assign(cartUrl); }, 50);
      }
    }

    // Lock approval-banner % to the RECOMMENDED cart (state.qty AFTER applyRecommendation).
    // Called once on Q9 entry; stepper changes thereafter never touch the locked %.
    function lockApprovedPct() {
      const recItems = Object.keys(state.qty).filter(k => state.qty[k] > 0);
      const recUnique = recItems.length;
      const recTotalQty = recItems.reduce((s, k) => s + state.qty[k], 0);
      if (recTotalQty === 0) return;
      const recSubtotalOtp   = round(recItems.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].otp, 0));
      const recAfterSub      = round(recItems.reduce((s, k) => s + state.qty[k] * PRICE_TABLE[k].sub, 0));
      const recBundlePct     = recTotalQty >= 3 ? BUNDLE_3_PCT : (recTotalQty === 2 ? BUNDLE_2_PCT : 0);
      const recBundleAmt     = round(recAfterSub * recBundlePct / 100);
      const recWellness      = recUnique === TOTAL_PRODUCTS   ? WELLNESS_BONUS_FULL
                              : recUnique === TOTAL_PRODUCTS - 1 ? WELLNESS_BONUS_THREE
                              : 0;
      const recCleanEnergy   = (state.cleanEnergyEligible && (state.qty.energy || 0) >= 2 && recSubtotalOtp >= 150)
                              ? CLEAN_ENERGY_BONUS : 0;
      const recFinal         = round(recAfterSub - recBundleAmt - recWellness - recCleanEnergy);
      const recPct           = Math.round(((recSubtotalOtp - recFinal) / recSubtotalOtp) * 100);
      root.querySelectorAll("[data-cx-approved-pct]").forEach(el => {
        el.textContent = String(recPct);
      });
    }

    screens.forEach((s, i) => { if (i === 0) s.classList.add("is-active"); });
    updateProgress();
    updateReceipt();
    lockApprovedPct();
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-cx-quiz-root]").forEach(init);
  });

  window.CXQuiz = { init };
})();
